import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Transition from 'react-transition-group/Transition';
import noop from 'lodash/noop';
import NavigationArrow from '@adsk/bim360-matrix-react-navigation-arrow';

import './_Carousel.scss';

const NavigationArrowHOC = isPrevious => {
  const NavigationArrowWrapper = props => (
    <NavigationArrow iconSize={32} isPrevious={isPrevious} {...props} />
  );

  return NavigationArrowWrapper;
};

export default class Carousel extends Component {
  _getSlide(isPrevious) {
    const children = this._getChildren(this.props);

    if (isPrevious && this.state.slide > 0) {
      return this.state.slide - 1;
    } else if (isPrevious && this.props.infinite) {
      return children.length - 1;
    } else if (!isPrevious && this.state.slide < children.length - 1) {
      return this.state.slide + 1;
    } else {
      return 0;
    }
  }

  _change(isPrevious) {
    const newState = { slide: this._getSlide(isPrevious) };

    this.props.beforeChange(this.state.slide, newState.slide);

    this.setState(newState);
  }

  _shouldShowArrow(isPrevious) {
    const children = this._getChildren(this.props);
    //     (   is infinite and more than one element  ) OR (     haven't reached the start    ) OR (               haven't reached the end               )
    return (
      (this.props.infinite && children.length > 1) ||
      (isPrevious && this.state.slide > 0) ||
      (!isPrevious && this.state.slide < children.length - 1)
    );
  }

  _renderArrow(isPrevious) {
    const Arrow = this.props.arrowRenderer(isPrevious);
    const classes = classNames(
      'Carousel__navigation-arrow',
      `Carousel__arrow-${isPrevious ? 'previous' : 'next'}`,
      { 'Carousel__navigation-arrow--hidden': !this._shouldShowArrow(isPrevious) }
    );
    return <Arrow className={classes} onClick={() => this._change(isPrevious)} />;
  }

  _getChildren(props) {
    return Array.isArray(props.children) ? props.children : [props.children];
  }

  next() {
    this._change(false);
  }

  prev() {
    this._change(true);
  }

  constructor(props) {
    super(props);

    this.state = {
      slide: props.slide,
      transform: null
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // In case the given slide isn't an index of an actual child, fallback to the first one.
    const nextSlide = this._getChildren(nextProps)[nextProps.slide] ? nextProps.slide : 0;

    if (nextSlide !== this.props.slide) {
      this.setState({ slide: nextSlide });
    }
  }

  render() {
    const children = this._getChildren(this.props);
    return (
      <div className={`Carousel ${this.props.className}`}>
        {this._renderArrow(true)}
        <div className="Carousel__track">
          {children.map((child, index) => {
            return (
              <Transition
                key={index}
                in={index === this.state.slide}
                timeout={this.props.timeout}
                onEntered={() => this.props.afterChange(this.state.slide)}
              >
                <div
                  className="Carousel__item-container"
                  style={{
                    transition: `transform ${this.props.timeout}ms`,
                    transform: `translateX(${-100 * this.state.slide}%)`
                  }}
                >
                  {React.cloneElement(child)}
                </div>
              </Transition>
            );
          })}
        </div>
        {this._renderArrow(false)}
      </div>
    );
  }
}

Carousel.defaultProps = {
  arrowRenderer: NavigationArrowHOC,
  slide: 0,
  timeout: 500,
  infinite: false,
  beforeChange: noop,
  afterChange: noop
};

Carousel.propTypes = {
  /**
   * The elements to scroll through the Carousel.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  /**
   * A function to render the arrows in the carousel.
   * function(isPrevious) => React.Component
   */
  arrowRenderer: PropTypes.func,
  /**
   * The slide index to show as the current item.
   */
  slide: PropTypes.number,
  /**
   * The timeout (in ms) for the transition between the slides
   */
  timeout: PropTypes.number,
  /**
   * Whether or not the Carousel should have an infinite navigation (the next of the last is the first and the previous of the first is that last)
   */
  infinite: PropTypes.bool,
  /**
   * Classname to add to the entire component (for more fine grained control of the layout of the carousel)
   */
  className: PropTypes.string,
  /**
   * A callback that's called before changing the current slide.
   * function(currentSlide, nextSlide)
   */
  beforeChange: PropTypes.func,
  /**
   * A callback that's called right after the animation has finished and the current slide has changed.
   * function(currentSlide)
   */
  afterChange: PropTypes.func
};
